import { toast } from "@/hooks/use-toast";

// Error categories for better organization and handling
export enum ErrorCategory {
  NETWORK = "network",
  AUTHENTICATION = "authentication",
  VALIDATION = "validation",
  FILE_PROCESSING = "file_processing",
  FILE_UPLOAD = "file_upload",
  API = "api",
  DATABASE = "database",
  UNKNOWN = "unknown"
}

// Interface for structured error information
export interface ErrorInfo {
  message: string;
  category: ErrorCategory;
  details?: string;
  originalError?: unknown;
  shouldToast?: boolean;
  toastTitle?: string;
}

/**
 * Formats an error into a standardized error object
 */
export function formatError(error: unknown, category: ErrorCategory = ErrorCategory.UNKNOWN): ErrorInfo {
  // Handle Error objects
  if (error instanceof Error) {
    return {
      message: error.message,
      category,
      details: error.stack,
      originalError: error
    };
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    return {
      message: error,
      category
    };
  }
  
  // Handle objects with message property
  if (error && typeof error === 'object' && 'message' in error) {
    return {
      message: String(error.message),
      category,
      originalError: error
    };
  }
  
  // Default case for unknown error types
  return {
    message: 'An unknown error occurred',
    category: ErrorCategory.UNKNOWN,
    originalError: error
  };
}

/**
 * Categorizes an error based on its message or type
 */
export function categorizeError(error: unknown): ErrorCategory {
  const errorMessage = error instanceof Error ? error.message.toLowerCase() : 
                      typeof error === 'string' ? error.toLowerCase() : '';
  
  if (errorMessage.includes('network') || errorMessage.includes('fetch') || 
      errorMessage.includes('connection') || errorMessage.includes('timeout')) {
    return ErrorCategory.NETWORK;
  }
  
  if (errorMessage.includes('auth') || errorMessage.includes('login') || 
      errorMessage.includes('permission') || errorMessage.includes('unauthorized') ||
      errorMessage.includes('forbidden')) {
    return ErrorCategory.AUTHENTICATION;
  }
  
  if (errorMessage.includes('validation') || errorMessage.includes('invalid') || 
      errorMessage.includes('required')) {
    return ErrorCategory.VALIDATION;
  }
  
  if (errorMessage.includes('file process') || errorMessage.includes('parsing')) {
    return ErrorCategory.FILE_PROCESSING;
  }
  
  if (errorMessage.includes('upload') || errorMessage.includes('file size')) {
    return ErrorCategory.FILE_UPLOAD;
  }
  
  if (errorMessage.includes('api') || errorMessage.includes('endpoint')) {
    return ErrorCategory.API;
  }
  
  if (errorMessage.includes('database') || errorMessage.includes('query') || 
      errorMessage.includes('supabase')) {
    return ErrorCategory.DATABASE;
  }
  
  return ErrorCategory.UNKNOWN;
}

/**
 * Gets a user-friendly error message based on the error category
 */
export function getUserFriendlyMessage(errorInfo: ErrorInfo): string {
  switch (errorInfo.category) {
    case ErrorCategory.NETWORK:
      return "Network error. Please check your internet connection and try again.";
    
    case ErrorCategory.AUTHENTICATION:
      return "Authentication error. Please log in again.";
    
    case ErrorCategory.VALIDATION:
      return errorInfo.message || "Invalid input. Please check your data and try again.";
    
    case ErrorCategory.FILE_PROCESSING:
      return errorInfo.message || "Error processing file. The file might be corrupted or in an unsupported format.";
    
    case ErrorCategory.FILE_UPLOAD:
      return errorInfo.message || "Error uploading file. Please try again.";
    
    case ErrorCategory.API:
      return "Server error. Please try again later.";
    
    case ErrorCategory.DATABASE:
      return "Database error. Please try again later.";
    
    case ErrorCategory.UNKNOWN:
    default:
      return errorInfo.message || "An unexpected error occurred. Please try again.";
  }
}

/**
 * Logs an error to the console with standardized format
 */
export function logError(error: unknown, context?: string): ErrorInfo {
  const category = categorizeError(error);
  const errorInfo = formatError(error, category);

  return errorInfo;
}

/**
 * Displays a toast notification for an error
 */
export function showErrorToast(errorInfo: ErrorInfo): void {
  toast({
    variant: "destructive",
    title: errorInfo.toastTitle || `Error: ${errorInfo.category}`,
    description: getUserFriendlyMessage(errorInfo),
  });
}

/**
 * Main error handler function that logs the error and optionally shows a toast
 */
export function handleError(
  error: unknown, 
  context?: string, 
  options: { 
    showToast?: boolean, 
    toastTitle?: string,
    category?: ErrorCategory
  } = {}
): ErrorInfo {
  const category = options.category || categorizeError(error);
  const errorInfo = formatError(error, category);
  
  if (options.toastTitle) {
    errorInfo.toastTitle = options.toastTitle;
  }
  
  // Log the error
  logError(errorInfo, context);
  
  // Show toast if requested
  if (options.showToast !== false) {
    showErrorToast(errorInfo);
  }
  
  return errorInfo;
}

/**
 * Creates a try-catch wrapper for async functions
 */
export function withErrorHandling<T, Args extends any[]>(
  fn: (...args: Args) => Promise<T>,
  context: string,
  options: { 
    showToast?: boolean, 
    toastTitle?: string,
    category?: ErrorCategory,
    fallbackValue?: T
  } = {}
): (...args: Args) => Promise<T | undefined> {
  return async (...args: Args) => {
    try {
      return await fn(...args);
    } catch (error) {
      handleError(error, context, options);
      return options.fallbackValue;
    }
  };
} 

export const handleSupabaseError = (error: any, context?: string) => {

  // Check for specific error patterns
  if (error?.message?.includes('400') && error?.message?.includes('Bad Request')) {
    // Likely an invalid ID format
    toast({
      title: "Invalid Request",
      description: "The requested resource could not be found. Please check the URL and try again.",
      variant: "destructive",
    });
    return;
  }

  if (error?.message?.includes('Failed to fetch posit')) {
    toast({
      title: "Posit Not Found",
      description: "The requested posit could not be found or you don't have access to it.",
      variant: "destructive",
    });
    return;
  }

  // Generic error handling
  toast({
    title: "Error",
    description: error?.message || "An unexpected error occurred. Please try again.",
    variant: "destructive",
  });
};

export const isValidSupabaseResponse = (data: any, error: any): boolean => {
  if (error) {
    handleSupabaseError(error);
    return false;
  }
  return true;
}; 